<template>
  <div class="tos-wrapper">
    <h1>Terms of Use</h1>

    <p class="effective-date">Effective as of: March 4, 2026</p>

    <p>
      These Terms of Use (the "Terms") govern your access to and use of the
      Cashless FYI calculators and related content (the "Service"), provided by
      Newcare International, LLC ("Newcare", "we", "us", or "our").
      By accessing or using the Service in any way, you agree to be bound by
      these Terms.
    </p>

    <h3>1. Informational Use Only – No Professional Advice</h3>
    <p>
      The calculators and analysis provided through the Service are for
      informational and illustrative purposes only. They are intended to help
      you explore potential financial outcomes based on the assumptions and
      data you provide. The Service does not constitute financial, legal, tax,
      accounting, or investment advice, and you should not rely on it as a
      substitute for advice from qualified professionals familiar with your
      specific circumstances.
    </p>

    <h3>2. No Warranty on Accuracy or Results</h3>
    <p>
      We make reasonable efforts to design our calculators thoughtfully and to
      keep the underlying logic current; however, we do not guarantee that any
      output, projection, or result is accurate, complete, current, or
      suitable for your particular use. All results are dependent on the
      assumptions, inputs, and configuration you choose.
    </p>
    <p>
      The Service is provided on an "AS IS" and "AS AVAILABLE" basis, without
      any warranties of any kind, whether express, implied, statutory, or
      otherwise, including without limitation any implied warranties of
      accuracy, fitness for a particular purpose, merchantability, title, or
      non‑infringement. You are solely responsible for independently
      evaluating the outputs and determining how (or whether) to rely on or
      act on them.
    </p>

    <h3>3. Your Responsibilities</h3>
    <p>
      You are responsible for:
    </p>
    <ul>
      <li>Providing true, accurate, and complete data and assumptions;</li>
      <li>Reviewing all outputs, projections, and analysis for reasonableness;</li>
      <li>
        Determining any business, financial, or operational decisions you make
        in light of your own professional advice and internal review
        processes.
      </li>
    </ul>
    <p>
      We are not responsible for any decisions you make, or any actions you
      take or fail to take, based on the information generated by the Service.
    </p>

    <h3>4. Limitation of Liability</h3>
    <p>
      To the maximum extent permitted by applicable law, in no event will
      Newcare, its affiliates, or their respective owners, officers,
      employees, or agents be liable for any indirect, incidental, special,
      consequential, exemplary, or punitive damages, or for any loss of
      profits, revenue, data, or business opportunities, arising out of or in
      connection with your use of, or inability to use, the Service—even if we
      have been advised of the possibility of such damages.
    </p>
    <p>
      To the extent any liability is found notwithstanding the foregoing, our
      total aggregate liability for all claims relating to the Service will be
      limited to one hundred U.S. dollars (USD $100).
    </p>

    <h3>5. Privacy and Data Stewardship</h3>
    <p>
      We take the stewardship of your information seriously. Our collection,
      use, and protection of personal information is described in our Privacy
      Policy, which is incorporated into these Terms by reference. By using
      the Service, you also agree to the Privacy Policy.
    </p>

    <h3>6. Acceptable Use</h3>
    <p>
      You agree not to use the Service in any way that:
    </p>
    <ul>
      <li>Violates any applicable law or regulation;</li>
      <li>
        Attempts to reverse engineer, decompile, or otherwise derive the
        underlying source code or models of the calculators;
      </li>
      <li>
        Interferes with or disrupts the normal operation or security of the
        Service;
      </li>
      <li>
        Infringes or misappropriates the intellectual property or privacy
        rights of any third party.
      </li>
    </ul>

    <h3>7. Intellectual Property</h3>
    <p>
      The Service, including all calculators, underlying logic, text, graphics,
      and other content, is owned by Newcare or its licensors and is protected
      by copyright and other intellectual property laws. You are granted a
      limited, revocable, non‑exclusive, non‑transferable license to access
      and use the Service solely for your internal evaluation and planning
      purposes. No other rights are granted.
    </p>

    <h3>8. Changes to the Service and These Terms</h3>
    <p>
      We may update or modify the Service, and these Terms, from time to time.
      When we make material changes, we will update the "Effective as of" date
      above and may provide additional notice (for example, via the site
      footer). Your continued use of the Service after any such changes
      constitutes your acceptance of the updated Terms.
    </p>

    <h3>9. Governing Law and Venue</h3>
    <p>
      These Terms are governed by and construed in accordance with the laws of
      the State of Mississippi, United States of America, without regard to
      its conflict of law rules. Any dispute arising out of or relating to the
      Service or these Terms will be brought exclusively in the state or
      federal courts located in Mississippi, and you consent to the personal
      jurisdiction and venue of those courts.
    </p>

    <h3>10. Contact</h3>
    <p>
      If you have questions about these Terms or the Service, you may contact
      us using the contact details provided on the site.
    </p>
  </div>
</template>

<script>
export default {
  name: "TermsOfUse",
};
</script>

<style scoped lang="scss">
@import '@/assets/css/app.scss';

.tos-wrapper {
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  text-align: left;
  color: $body-color;
}

h1 {
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-align: center;
}

.effective-date {
  text-align: center;
  font-size: 0.9rem;
  color: $secondary;
  margin-bottom: 2rem;
}

h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 1.75rem;
  margin-bottom: 0.75rem;
}

p {
  margin-bottom: 0.85rem;
  line-height: 1.6;
}

ul {
  margin: 0 0 0.85rem 1.25rem;
  padding: 0;

  li {
    margin-bottom: 0.35rem;
  }
}
</style>

